import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plug, ExternalLink, CheckCircle2, Clock, TriangleAlert, RefreshCw, Trash2, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

// Provider logo pill: monogram on brand colour. Deliberately simple: keeps
// the page dependency-free of third-party logo files and always renders
// crisp on the dark glass background.
function ProviderMark({ provider }) {
  const bg = provider.brand_color || "#c8a96a";
  const letter = provider.name.charAt(0);
  return (
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center font-display text-xl"
      style={{
        background: `linear-gradient(180deg, ${bg}18, ${bg}05)`,
        border: `1px solid ${bg}55`,
        color: "#f5efe0",
      }}
      aria-hidden
    >
      {letter}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    connected: { label: "Connected", color: "text-emerald-300", bg: "bg-emerald-900/30", border: "border-emerald-500/40", Icon: CheckCircle2 },
    awaiting_credentials: { label: "Awaiting credentials", color: "text-amber-200", bg: "bg-amber-900/25", border: "border-amber-500/40", Icon: Clock },
    disconnected: { label: "Disconnected", color: "text-white/60", bg: "bg-white/5", border: "border-white/15", Icon: TriangleAlert },
    error: { label: "Error", color: "text-rose-200", bg: "bg-rose-900/30", border: "border-rose-500/40", Icon: TriangleAlert },
    not_set_up: { label: "Not set up", color: "text-white/50", bg: "bg-white/5", border: "border-white/10", Icon: Plug },
  };
  const s = map[status] || map.not_set_up;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tabular ${s.color} ${s.bg} border ${s.border}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  );
}

function formatTs(ts) {
  if (!ts) return "-";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

function formatMoney(n, currency = "USD") {
  try {
    return Number(n).toLocaleString(undefined, { style: "currency", currency });
  } catch {
    return `${currency} ${Number(n).toFixed(2)}`;
  }
}

export default function PosConnect() {
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // provider id currently syncing
  const [dialog, setDialog] = useState(null); // { provider, existing }

  const load = useCallback(async () => {
    try {
      const [p, c, s] = await Promise.all([
        api.get("/pos/providers"),
        api.get("/pos/connections"),
        api.get("/pos/sales", { params: { limit: 25 } }),
      ]);
      setProviders(p.data?.providers || []);
      setConnections(c.data || []);
      setSales(s.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load POS hub.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const connByProvider = useMemo(() => {
    const map = {};
    for (const c of connections) map[c.provider] = c;
    return map;
  }, [connections]);

  const handleConnect = (provider) => {
    setDialog({ provider, existing: connByProvider[provider.id] || null });
  };

  const submitConnect = async ({ account_ref, label, access_token, sandbox }) => {
    if (!dialog) return;
    const provider = dialog.provider;
    try {
      const payload = {
        provider: provider.id,
        account_ref: account_ref || null,
        label: label || null,
        sandbox: !!sandbox,
      };
      // Only send access_token when the user actually typed one. Sending
      // null keeps the previously saved token untouched.
      if (access_token) payload.access_token = access_token;
      await api.post("/pos/connections", payload);
      toast.success(`${provider.name} saved. Run Sync now to pull live orders.`);
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save connection.");
    }
  };

  const handleSync = async (provider, sample = false) => {
    const conn = connByProvider[provider.id];
    if (!conn) return handleConnect(provider);
    setBusy(provider.id);
    try {
      const path = sample ? `/pos/sync/${provider.id}/sample` : `/pos/sync/${provider.id}`;
      const res = await api.post(path);
      const { status, message, sales_synced } = res.data || {};
      if (status === "ok") {
        toast.success(`${provider.name}: ${sales_synced ?? 0} sales synced.`);
      } else if (status === "pending_credentials") {
        toast.message(`${provider.name}: awaiting credentials`, { description: message });
      } else {
        toast.error(`${provider.name} sync: ${message || "unknown status"}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Sync failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async (conn, provider) => {
    if (!window.confirm(`Disconnect ${provider.name}? Historical sales stay saved.`)) return;
    try {
      await api.delete(`/pos/connections/${conn.id}`);
      toast.success(`${provider.name} disconnected.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not disconnect.");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading POS hub
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl mx-auto space-y-8" data-testid="pos-connect-page">
      <div>
        <div className="label-eyebrow gold flex items-center gap-2">
          <Plug className="w-3.5 h-3.5" /> Point of Sale
        </div>
        <h1 className="font-display text-3xl md:text-4xl mt-2 tracking-tight">Connect your POS</h1>
        <p className="text-white/60 mt-3 max-w-2xl text-sm md:text-base">
          One universal hub for every provider. Sales land in the same schema regardless of the system your kitchen runs, so Inventory, Costing and the Ledger keep working the same for you and the next site you open.
        </p>
        <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-100/90 flex items-start gap-2" data-testid="pos-plan-note">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Please ensure your POS plan supports API integrations. Square, Toast and Clover each require an active developer or Pro plan on their end to issue an access token. Costs vary by provider.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pos-provider-grid">
        {providers.map((p) => {
          const conn = connByProvider[p.id];
          const status = conn?.status || "not_set_up";
          const isBusy = busy === p.id;
          return (
            <div
              key={p.id}
              className="rounded-2xl glass-card p-5 flex flex-col gap-4"
              data-testid={`pos-provider-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <ProviderMark provider={p} />
                  <div>
                    <div className="font-display text-xl leading-none">{p.name}</div>
                    <div className="text-[11px] text-white/50 mt-1">{p.tagline}</div>
                  </div>
                </div>
                <StatusChip status={status} />
              </div>

              {conn && (
                <div className="text-xs text-white/60 space-y-1">
                  {conn.label && (
                    <div className="flex justify-between gap-2">
                      <span className="text-white/40">Label</span>
                      <span className="text-white/80 truncate">{conn.label}</span>
                    </div>
                  )}
                  {conn.account_ref && (
                    <div className="flex justify-between gap-2">
                      <span className="text-white/40">{p.account_hint}</span>
                      <span className="tabular text-white/80 truncate">{conn.account_ref}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Last sync</span>
                    <span className="tabular text-white/80">{formatTs(conn.last_sync_at)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Sales synced</span>
                    <span className="tabular text-white/80">{conn.sales_synced || 0}</span>
                  </div>
                  {conn.last_sync_message && (
                    <div className="text-[11px] text-white/50 mt-2 leading-relaxed">
                      {conn.last_sync_message}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-auto flex flex-wrap gap-2 pt-1">
                {conn ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleSync(p, false)}
                      disabled={isBusy}
                      data-testid={`pos-sync-${p.id}`}
                    >
                      {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      <span className="ml-1.5">Sync now</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleSync(p, true)}
                      disabled={isBusy}
                      data-testid={`pos-sample-${p.id}`}
                    >
                      Test sync
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleConnect(p)}
                      data-testid={`pos-edit-${p.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-300 hover:text-rose-200"
                      onClick={() => handleDisconnect(conn, p)}
                      data-testid={`pos-disconnect-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleConnect(p)}
                    data-testid={`pos-connect-${p.id}`}
                  >
                    <Plug className="w-3.5 h-3.5" />
                    <span className="ml-1.5">Connect {p.name}</span>
                  </Button>
                )}
                <a
                  href={p.docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-[11px] inline-flex items-center gap-1 text-white/40 hover:text-white/70"
                >
                  API docs <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl glass-card p-5" data-testid="pos-sales-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow gold">Recent sales</div>
            <div className="text-xs text-white/50 mt-1">Unified feed across every connected provider.</div>
          </div>
          <div className="text-[11px] text-white/40 tabular">{sales.length} row{sales.length === 1 ? "" : "s"}</div>
        </div>

        {sales.length === 0 ? (
          <div className="text-sm text-white/50 py-8 text-center">
            No sales synced yet. Connect a provider above, then run Sync now or Test sync.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pos-sales-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                  <th className="text-left py-2 pr-3">Item</th>
                  <th className="text-left py-2 pr-3">Provider</th>
                  <th className="text-right py-2 pr-3">Qty</th>
                  <th className="text-right py-2 pr-3">Price</th>
                  <th className="text-right py-2">Sold at</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((s) => (
                  <tr key={s.id} className="border-t border-white/5">
                    <td className="py-2 pr-3 text-white/90">{s.item_name}</td>
                    <td className="py-2 pr-3 text-white/60 capitalize">{s.provider}</td>
                    <td className="py-2 pr-3 text-right tabular text-white/80">{s.quantity}</td>
                    <td className="py-2 pr-3 text-right tabular text-white/80">{formatMoney(s.price, s.currency)}</td>
                    <td className="py-2 text-right tabular text-white/60">{formatTs(s.sold_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConnectDialog
        state={dialog}
        onClose={() => setDialog(null)}
        onSubmit={submitConnect}
      />
    </div>
  );
}

function ConnectDialog({ state, onClose, onSubmit }) {
  const [accountRef, setAccountRef] = useState("");
  const [label, setLabel] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [sandbox, setSandbox] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState(null);

  useEffect(() => {
    if (state) {
      setAccountRef(state.existing?.account_ref || "");
      setLabel(state.existing?.label || "");
      setAccessToken("");
      setSandbox(!!state.existing?.sandbox);
      setVerifyMsg(null);
    }
  }, [state]);

  if (!state) return null;
  const provider = state.provider;
  const hasSavedToken = !!state.existing?.has_token;
  const tokenLast4 = state.existing?.token_last4;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onSubmit({
      account_ref: accountRef.trim(),
      label: label.trim(),
      access_token: accessToken.trim() || null,
      sandbox,
    });
    setSubmitting(false);
  };

  const verify = async () => {
    if (!accessToken.trim() || !accountRef.trim()) {
      setVerifyMsg({ ok: false, text: "Enter both the access token and the account reference to verify." });
      return;
    }
    setVerifying(true);
    setVerifyMsg(null);
    try {
      const res = await api.post("/pos/test-connection", {
        provider: provider.id,
        account_ref: accountRef.trim(),
        access_token: accessToken.trim(),
        sandbox,
      });
      setVerifyMsg({ ok: res.data.ok, text: res.data.message || (res.data.ok ? "Token valid." : "Verification failed.") });
    } catch (e) {
      setVerifyMsg({ ok: false, text: formatApiErrorDetail(e?.response?.data?.detail) || "Verification failed." });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pos-connect-dialog">
        <DialogHeader>
          <DialogTitle>Connect {provider.name}</DialogTitle>
          <DialogDescription>
            Paste your {provider.name} access token and account reference. We store the token encrypted server-side and never expose it back to the browser. You can verify the token before saving.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="pos-account-ref">{provider.account_hint}</Label>
            <Input
              id="pos-account-ref"
              value={accountRef}
              onChange={(e) => setAccountRef(e.target.value)}
              placeholder={`Your ${provider.account_hint}`}
              data-testid="pos-account-ref"
            />
          </div>
          <div>
            <Label htmlFor="pos-access-token">
              {provider.credential_hint}
              {hasSavedToken && <span className="ml-2 text-[10px] text-white/50">saved: ***{tokenLast4}</span>}
            </Label>
            <Input
              id="pos-access-token"
              type="password"
              value={accessToken}
              onChange={(e) => { setAccessToken(e.target.value); setVerifyMsg(null); }}
              placeholder={hasSavedToken ? "Leave blank to keep the saved token" : `Paste your ${provider.name} access token`}
              data-testid="pos-access-token"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label htmlFor="pos-label">Label (optional)</Label>
            <Input
              id="pos-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Main dining room"
              data-testid="pos-label"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer" data-testid="pos-sandbox-toggle">
            <input
              type="checkbox"
              checked={sandbox}
              onChange={(e) => setSandbox(e.target.checked)}
              className="rounded"
            />
            Use sandbox host (for {provider.name} test accounts)
          </label>
          <div className="text-[11px] text-white/50 leading-relaxed">
            Generate a token from the {provider.name}{" "}
            <a href={provider.docs_url} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:underline">
              developer docs
            </a>. Ensure your {provider.name} plan supports API integrations.
          </div>
          {verifyMsg && (
            <div
              className={`text-xs rounded-md px-3 py-2 border ${verifyMsg.ok ? "text-emerald-200 bg-emerald-900/20 border-emerald-500/40" : "text-rose-200 bg-rose-900/20 border-rose-500/40"}`}
              data-testid="pos-verify-result"
            >
              {verifyMsg.text}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              type="button"
              variant="secondary"
              onClick={verify}
              disabled={verifying || !accessToken.trim()}
              data-testid="pos-verify"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
            </Button>
            <Button type="submit" disabled={submitting} data-testid="pos-connect-save">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
